/**
 * Route Context - React Context for providing route functionality to the app tree.
 *
 * This context allows the main app to provide its useGetPageUrl function
 * to modules without them needing to import from src/.
 *
 * Architecture:
 * 1. Main app wraps components with RouteProvider, passing useGetPageUrl
 * 2. Modules use usePageUrls() to get access to page URLs
 * 3. No direct imports from src/ in modules
 *
 * Note: Hooks are exported from routeHooks.ts to satisfy react-refresh
 * Note: Context instance is in routeContextInstance.ts to satisfy react-refresh
 */

import React, { useMemo } from 'react';
import type {
  RouteContextValue,
  UseGetPageUrl,
  UseGeneratePageUrl,
} from './types';
import { RouteContext } from './routeContextInstance';

/**
 * Props for RouteProvider
 */
export interface RouteProviderProps {
  children: React.ReactNode;
  /**
   * The useGetPageUrl hook from the main app
   */
  useGetPageUrl: UseGetPageUrl;
  /**
   * Optional useGeneratePageUrl hook from the main app
   */
  useGeneratePageUrl?: UseGeneratePageUrl;
  /**
   * Optional navigation function
   */
  navigate?: (url: string) => void;
  /**
   * Optional current route parameters
   */
  currentParams?: RouteContextValue['currentParams'];
}

/**
 * Route Provider - Wraps the app to provide route context
 * The main app should use this at the root level
 */
export const RouteProvider: React.FC<RouteProviderProps> = ({
  children,
  useGetPageUrl,
  useGeneratePageUrl,
  navigate,
  currentParams,
}) => {
  const contextValue = useMemo<RouteContextValue>(
    () => ({
      useGetPageUrl,
      useGeneratePageUrl,
      navigate,
      currentParams,
    }),
    [useGetPageUrl, useGeneratePageUrl, navigate, currentParams],
  );

  return (
    <RouteContext.Provider value={contextValue}>
      {children}
    </RouteContext.Provider>
  );
};
